import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Cookie, X, Check, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { isChurchPublicPath } from "@/lib/church-public-routes";

export function CookieConsent() {
  const [isVisible, setIsVisible] = useState(false);
  // Página pública de igreja (bio, formulários): não carrega os rastreadores
  // de marketing, então não há o que perguntar. Ver church-public-routes.ts.
  const { pathname } = useLocation();
  const churchPublic = isChurchPublicPath(pathname);

  useEffect(() => {
    const consent = localStorage.getItem("ecclesiato-cookie-consent");
    if (!consent) {
      // Pequeno delay para não assustar o usuário logo no carregamento
      const timer = setTimeout(() => setIsVisible(true), 2000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem("ecclesiato-cookie-consent", "accepted");
    setIsVisible(false);
    // Dispara um evento customizado para que outros componentes saibam que podem carregar os scripts
    window.dispatchEvent(new Event("cookie-consent-updated"));
  };

  const handleDecline = () => {
    localStorage.setItem("ecclesiato-cookie-consent", "declined");
    setIsVisible(false);
  };

  return (
    <div>
      {isVisible && !churchPublic && (
        // No mobile este cartão dividia a tela com o CTA do hero e com a barra
        // fixa de preço (sticky-cta.tsx): ocupava ~260px do rodapé, cobria o
        // botão principal e o visitante não tinha o que tocar antes de decidir
        // sobre cookies. Aqui ele é compacto até o `md` — texto menor, menos
        // padding, botões na mesma linha — e só a partir do desktop volta a ser
        // o cartão folgado do canto.
        <div className="fixed bottom-3 left-3 right-3 md:bottom-6 md:left-auto md:right-8 md:w-100 z-9999">
          <Card className="gap-3 py-4 px-4 md:gap-5 md:py-6 md:px-6 border-primary/20 bg-background/95 md:bg-background/80 backdrop-blur-xl shadow-2xl relative overflow-hidden group">
            {/* Efeito de brilho sutil ao fundo */}
            <div className="absolute -top-24 -right-24 w-48 h-48 bg-primary/10 rounded-full blur-3xl group-hover:bg-primary/20 transition-colors duration-500" />

            <div className="flex items-start gap-3 md:gap-4 relative z-10 pr-7">
              <div className="p-2 md:p-3 rounded-xl md:rounded-2xl bg-primary/10 text-primary shrink-0">
                <Cookie className="h-5 w-5 md:h-6 md:w-6" />
              </div>
              <div className="space-y-1 md:space-y-2">
                <h3 className="font-semibold text-sm md:text-base text-foreground flex items-center gap-2">
                  Privacidade & Cookies
                  <ShieldCheck className="h-4 w-4 text-primary" />
                </h3>
                {/* Duas versões do mesmo aviso: a curta cabe em duas linhas no
                    mobile, onde cada linha extra empurra o CTA do hero para
                    fora da tela. O link para a política é o que o texto precisa
                    ter para cumprir a LGPD — ele fica nas duas. */}
                <p className="text-xs leading-snug text-muted-foreground md:hidden">
                  Usamos cookies para melhorar sua experiência e analisar o
                  tráfego. Veja a
                  <a
                    href="/politica-privacidade"
                    className="text-primary hover:underline ml-1"
                  >
                    Política de Privacidade
                  </a>
                  .
                </p>
                <p className="hidden md:block text-sm text-muted-foreground leading-relaxed">
                  Utilizamos cookies e tecnologias similares para melhorar sua
                  experiência, analisar o tráfego e personalizar conteúdos de
                  acordo com nossa
                  <a
                    href="/politica-privacidade"
                    className="text-primary hover:underline ml-1"
                  >
                    Política de Privacidade
                  </a>
                  .
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3 relative z-10">
              <Button
                variant="outline"
                className="flex-1 h-11 md:h-9 rounded-xl border-border/50 hover:bg-muted/50 text-sm"
                onClick={handleDecline}
              >
                Recusar
              </Button>
              <Button
                className="flex-1 h-11 md:h-9 rounded-xl shadow-lg shadow-primary/20 gap-2 text-sm"
                onClick={handleAccept}
              >
                <Check className="h-4 w-4" />
                Aceitar tudo
              </Button>
            </div>

            <button
              onClick={() => setIsVisible(false)}
              className="absolute top-1.5 right-1.5 md:top-2 md:right-2 p-2.5 text-muted-foreground hover:text-foreground transition-colors"
              aria-label="Fechar aviso de cookies"
            >
              <X className="h-4 w-4" aria-hidden="true" />
            </button>
          </Card>
        </div>
      )}
    </div>
  );
}
